/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CashflowFormulaFilters } from '../models/CashflowFormulaFilters';
import type { CashflowFormulaFiltersForEntity } from '../models/CashflowFormulaFiltersForEntity';
import type { CashflowFormulaTracebackRequest } from '../models/CashflowFormulaTracebackRequest';
import type { CommentaryFeedPropertyRead } from '../models/CommentaryFeedPropertyRead';
import type { FieldRead } from '../models/FieldRead';
import type { FormulaEntityResult } from '../models/FormulaEntityResult';
import type { FormulaFilters } from '../models/FormulaFilters';
import type { FormulaPayload } from '../models/FormulaPayload';
import type { FormulaRead } from '../models/FormulaRead';
import type { FormulaWithLx } from '../models/FormulaWithLx';
import type { LxAiAssistRequest } from '../models/LxAiAssistRequest';
import type { LxAiCancelRequest } from '../models/LxAiCancelRequest';
import type { LxExpandRequest } from '../models/LxExpandRequest';
import type { LxExpandResponse } from '../models/LxExpandResponse';
import type { LxFormatRequest } from '../models/LxFormatRequest';
import type { LxFormatResponse } from '../models/LxFormatResponse';
import type { PeriodTypeEnum } from '../models/PeriodTypeEnum';
import type { PropertyRead } from '../models/PropertyRead';
import type { Traceback } from '../models/Traceback';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class PropertiesService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * List Properties For Namespace Id
     * @param namespaceId
     * @param entityId
     * @returns PropertyRead Successful Response
     * @throws ApiError
     */
    public listPropertiesForNamespaceId(
        namespaceId: string,
        entityId?: (string | null),
    ): CancelablePromise<Array<PropertyRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/properties/list-properties-for-namespace-id/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            query: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Property
     * @param propertyId
     * @returns PropertyRead Successful Response
     * @throws ApiError
     */
    public getProperty(
        propertyId: string,
    ): CancelablePromise<PropertyRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/properties/get-property/{property_id}',
            path: {
                'property_id': propertyId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Formulas For Namespace Id
     * @param namespaceId
     * @returns FormulaWithLx Successful Response
     * @throws ApiError
     */
    public listFormulasForNamespaceId(
        namespaceId: string,
    ): CancelablePromise<Array<FormulaWithLx>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/properties/list-formulas-for-namespace-id/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Formula For Namespace Id
     * @param namespaceId
     * @param requestBody
     * @returns FormulaRead Successful Response
     * @throws ApiError
     */
    public createFormulaForNamespaceId(
        namespaceId: string,
        requestBody: FormulaPayload,
    ): CancelablePromise<FormulaRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/properties/create-formula-for-namespace-id/{namespace_id}',
            path: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Formula
     * @param formulaId
     * @param requestBody
     * @returns FormulaRead Successful Response
     * @throws ApiError
     */
    public saveFormula(
        formulaId: string,
        requestBody: FormulaPayload,
    ): CancelablePromise<FormulaRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/properties/save-formula/{formula_id}',
            path: {
                'formula_id': formulaId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Formulas With Values For Formula
     * @param clusterId
     * @param formulaId
     * @param requestBody
     * @returns FormulaEntityResult Successful Response
     * @throws ApiError
     */
    public listFormulasWithValuesForFormula(
        clusterId: string,
        formulaId: string,
        requestBody: FormulaFilters,
    ): CancelablePromise<Array<FormulaEntityResult>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/properties/list-formulas-with-values-for-formula',
            query: {
                'cluster_id': clusterId,
                'formula_id': formulaId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Traceback Cashflow Formula
     * @param clusterId
     * @param formulaId
     * @param entityId
     * @param requestBody
     * @returns Traceback Successful Response
     * @throws ApiError
     */
    public tracebackCashflowFormula(
        clusterId: string,
        formulaId: string,
        entityId: string,
        requestBody: CashflowFormulaTracebackRequest,
    ): CancelablePromise<Traceback> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/properties/traceback-cashflow-formula',
            query: {
                'cluster_id': clusterId,
                'formula_id': formulaId,
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cashflow Formulas With Values For Formula
     * Evaluate a cashflow formula using the resolver pipeline.
     *
     * This endpoint handles cashflow formulas (DPI, IRR, MOIC, etc.) that require
     * private cashflow metric materialization during resolution.
     * @param clusterId
     * @param formulaId
     * @param requestBody
     * @returns FormulaEntityResult Successful Response
     * @throws ApiError
     */
    public listCashflowFormulasWithValuesForFormula(
        clusterId: string,
        formulaId: string,
        requestBody: CashflowFormulaFilters,
    ): CancelablePromise<Array<FormulaEntityResult>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/properties/list-cashflow-formulas-with-values-for-formula',
            query: {
                'cluster_id': clusterId,
                'formula_id': formulaId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Commentary Feed Properties For Namespace Id
     * List all CommentaryFeedProperty entities for a given namespace
     * @param namespaceId
     * @returns CommentaryFeedPropertyRead Successful Response
     * @throws ApiError
     */
    public listCommentaryFeedPropertiesForNamespaceId(
        namespaceId: string,
    ): CancelablePromise<Array<CommentaryFeedPropertyRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/properties/list-commentary-feed-properties-for-namespace-id',
            query: {
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Commentary Feed Property
     * Create a new CommentaryFeedProperty entity
     * @param propertyId
     * @param clusterId
     * @param namespaceId
     * @returns CommentaryFeedPropertyRead Successful Response
     * @throws ApiError
     */
    public createCommentaryFeedProperty(
        propertyId: string,
        clusterId: string,
        namespaceId: string,
    ): CancelablePromise<CommentaryFeedPropertyRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/properties/create-commentary-feed-property',
            query: {
                'property_id': propertyId,
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Commentary Feed Property
     * Delete an CommentaryFeedProperty entity by ID
     * @param commentaryFeedPropertyId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteCommentaryFeedProperty(
        commentaryFeedPropertyId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/properties/delete-commentary-feed-property/{commentary_feed_property_id}',
            path: {
                'commentary_feed_property_id': commentaryFeedPropertyId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Format Lx
     * Format Lx expression code with consistent spacing and line breaks.
     * Returns the original code if formatting fails (safe mode).
     * @param requestBody
     * @returns LxFormatResponse Successful Response
     * @throws ApiError
     */
    public formatLx(
        requestBody: LxFormatRequest,
    ): CancelablePromise<LxFormatResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/properties/format-lx',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Expand Lx
     * Recursively expand property references in Lx expression using provided expansions.
     *
     * Replaces references like @formula-name or @bloc-123 with their definitions.
     * Useful for debugging and understanding complex nested expressions.
     *
     * Args:
     * request.lx: The Lx expression to expand
     * request.expansions: Dict mapping pointers to their definitions
     * e.g., {"@revenue-growth": "@revenue / @prior-revenue - 1"}
     *
     * Returns:
     * Fully expanded Lx expression with annotations showing what was expanded
     * @param requestBody
     * @returns LxExpandResponse Successful Response
     * @throws ApiError
     */
    public expandLx(
        requestBody: LxExpandRequest,
    ): CancelablePromise<LxExpandResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/properties/expand-lx',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Cancel Ai Assist Lx
     * Cancel an in-progress Lu AI generation.
     *
     * Sets a cancellation flag in Redis that the streaming task checks periodically.
     * The actual cancellation happens asynchronously - the task will stop at the next
     * event boundary and send a "stopped" message via WebSocket.
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public cancelAiAssistLx(
        requestBody: LxAiCancelRequest,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/properties/cancel-ai-assist-lx',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Properties For Entity
     * @param entityId
     * @returns PropertyRead Successful Response
     * @throws ApiError
     */
    public listPropertiesForEntity(
        entityId: string,
    ): CancelablePromise<Array<PropertyRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/properties/list-properties-for-entity',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Property Id By Asset Account Id
     * @param entityId
     * @returns string Successful Response
     * @throws ApiError
     */
    public getPropertyIdByAssetAccountId(
        entityId: string,
    ): CancelablePromise<Record<string, string>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/properties/get-property-id-by-asset-account-id',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Formulas With Values For Entity
     * @param entityId
     * @param startDate
     * @param endDate
     * @param scenarioId
     * @param periodType
     * @param globalAsOfDate
     * @returns FormulaEntityResult Successful Response
     * @throws ApiError
     */
    public listFormulasWithValuesForEntity(
        entityId: string,
        startDate: string,
        endDate: string,
        scenarioId?: (string | null),
        periodType?: (PeriodTypeEnum | null),
        globalAsOfDate?: (string | null),
    ): CancelablePromise<Array<FormulaEntityResult>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/properties/list-formulas-with-values-for-entity/{start_date}/{end_date}',
            path: {
                'entity_id': entityId,
                'start_date': startDate,
                'end_date': endDate,
            },
            query: {
                'scenario_id': scenarioId,
                'period_type': periodType,
                'global_as_of_date': globalAsOfDate,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cashflow Formulas With Values For Entity
     * Evaluate all cashflow formulas for an entity using the resolver pipeline.
     *
     * This endpoint returns all cashflow formulas (DPI, IRR, MOIC, etc.) applicable to
     * the entity, evaluated with private cashflow metric materialization.
     * @param entityId
     * @param requestBody
     * @returns FormulaEntityResult Successful Response
     * @throws ApiError
     */
    public listCashflowFormulasWithValuesForEntity(
        entityId: string,
        requestBody: CashflowFormulaFiltersForEntity,
    ): CancelablePromise<Array<FormulaEntityResult>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/properties/list-cashflow-formulas-with-values-for-entity',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Fields For Cluster
     * @param clusterId
     * @param includesTsm
     * @returns FieldRead Successful Response
     * @throws ApiError
     */
    public listFieldsForCluster(
        clusterId: string,
        includesTsm: boolean = false,
    ): CancelablePromise<Array<FieldRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/properties/list-fields-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'includes_tsm': includesTsm,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Formulas For Cluster
     * @param clusterId
     * @returns FormulaRead Successful Response
     * @throws ApiError
     */
    public listFormulasForCluster(
        clusterId: string,
    ): CancelablePromise<Array<FormulaRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/properties/list-formulas-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Properties For Cluster
     * @param clusterId
     * @returns PropertyRead Successful Response
     * @throws ApiError
     */
    public listPropertiesForCluster(
        clusterId: string,
    ): CancelablePromise<Array<PropertyRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/properties/list-properties-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Ai Assist Lx
     * Lu - AI assistant for generating Lx expressions.
     *
     * Uses agent architecture with:
     * - Orchestrator focused on workflow and coordination
     * - Tools encapsulate domain knowledge
     * - Property search with semantic understanding
     *
     * Returns immediately and streams response via WebSocket on 'LU' channel.
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public aiAssistLx(
        clusterId: string,
        requestBody: LxAiAssistRequest,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/properties/ai-assist-lx',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
